import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ConnectedOfficeBrand, connectedOfficeTheme, useConnectedOfficeBrand } from '../../n9-workspace-platform/shared/ConnectedOfficeBrand.jsx';
import AdministrativeExecution from './AdministrativeExecution.jsx';
import { confirmedLogout } from '../../n9-workspace-platform/shared/public-entry.js';
import {
  ArrowClockwise,
  ArrowLeft,
  ArrowRight,
  ArrowSquareOut,
  CheckCircle,
  Clock,
  CloudArrowUp,
  Database,
  DesktopTower,
  DownloadSimple,
  Eye,
  EyeSlash,
  FileXls,
  FileDoc,
  FolderOpen,
  FunnelSimple,
  GitBranch,
  HardDrives,
  LinkSimple,
  ListChecks,
  LockKey,
  MagnifyingGlass,
  Package,
  Play,
  Pulse,
  ShieldCheck,
  SignOut,
  SquaresFour,
  TerminalWindow,
  User,
  WarningCircle,
  WindowsLogo,
  X,
  XCircle,
} from "@phosphor-icons/react";
import { apiRequest } from "./api.js";
import { GoogleSignInButton } from "./GoogleSignInButton.jsx";
import { loginNotice } from "./auth-state.js";
import { buildLaunchCommand } from './launch-file.js';
import { referenceInput, engineRuntime } from './engine-input.js';
import JeddahControls from './JeddahControls.jsx';
import JeddahInquiryControls from './JeddahInquiryControls.jsx';
import { dataEngineOptions } from '../worker/data-engine-options.js';
import { savedPleadingPayload } from './saved-pleading.js';
const PleadingStudio = lazy(() => import('./PleadingStudio.jsx'));
const RulingControls = lazy(() => import('./RulingControls.jsx'));
const ViolationPreparation = lazy(() => import('./ViolationPreparation.jsx'));

const STATUS_LABELS = Object.freeze({
  ready: "جاهزة",
  queued: "بانتظار التشغيل",
  running: "قيد التشغيل",
  completed: "مكتملة",
  failed: "تعذرت",
  cancelled: "ملغاة",
});
const RUN_MODE_LABELS = { full: 'تشغيل كامل', refresh: 'إعادة فحص', test: 'تجريبي محدود', resume: 'استكمال' };

function jobStatusLabel(job) {
  if (job.stage === 'excel_upload_pending') return 'السحب نجح، Excel معلّق';
  if (job.stage === 'case_packages_upload_pending') return 'Excel جاهز، المرفقات معلّقة';
  if (job.stage === 'partial_result') return 'نتائج جزئية';
  return STATUS_LABELS[job.status] || job.status;
}

const STAGE_LABELS = Object.freeze({
  prepared: "تجهيز المدخلات",
  waiting_for_device: "انتظار وكيل الجهاز",
  waiting_for_resource: "انتظار مورد مشترك",
  waiting_for_cloud: 'انتظار خادم المحركات',
  agent_connected: "اتصال الوكيل",
  preparing_runtime: "تجهيز بيئة التشغيل",
  waiting_for_login: "انتظار تسجيل الدخول",
  browser_started: "المتصفح مفتوح",
  collecting: "جمع البيانات",
  exporting: "تجهيز Excel",
  uploading: "رفع النتيجة",
  local_result_ready: "اكتمل السحب محليًا",
  excel_uploaded: "رُفع Excel",
  excel_upload_pending: "رفع Excel يحتاج استكمالًا",
  case_packages_upload_pending: "رفع المرفقات يحتاج استكمالًا",
  case_packages_saved: "رُفعت المرفقات",
  diagnostics_saved: "حفظ سجل الطرفية",
  completed: "اكتمل",
  partial_result: "نتيجة جزئية محفوظة",
  failed: "تعذر التشغيل",
  cancelled: "ألغيت الوظيفة",
});

const CONTROL_LABELS = Object.freeze({
  enforced: "مطبق",
  monitored: "مراقب",
  planned: "مخطط",
});

const AUDIT_LABELS = Object.freeze({
  "engine.job.created": "إنشاء وظيفة محرك",
  "engine.job.launched": "إطلاق وظيفة محرك",
  "engine.job.cancelled": "إلغاء وظيفة محرك",
  "agent.job.claimed": "اتصال جهاز بالوظيفة",
  "agent.job.completed": "اكتمال وظيفة المحرك",
  "agent.runtime_log.saved": "حفظ سجل التشخيص",
  "agent.device.revoked": "سحب جهاز محلي",
});

function formatDate(value) {
  if (!value) return "لم يبدأ";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return new Intl.DateTimeFormat("ar-SA", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function triggerProtocol(url) {
  const link = document.createElement("a");
  link.href = url;
  link.hidden = true;
  document.body.appendChild(link);
  link.click();
  window.setTimeout(() => link.remove(), 1000);
}

function Toast({ message }) {
  if (!message) return null;
  return <div className="toast" role="status">{message}</div>;
}

function describeLoginError(error) {
  const messages = {
    AUTH_TEMPORARILY_LOCKED: "تم إيقاف محاولات الدخول مؤقتًا لحماية الحساب.",
    INVALID_CREDENTIALS: "اسم المستخدم أو كلمة المرور غير صحيحة.",
    REQUEST_TIMEOUT: "تأخر رد خدمة الدخول، وتم إيقاف الانتظار. أعد المحاولة الآن.",
    NETWORK_ERROR: "تعذر الاتصال بخدمة الهوية. تحقق من الشبكة ثم أعد المحاولة.",
    GOOGLE_PROVIDER_UNAVAILABLE: "تعذر الوصول إلى Google الآن. استخدم اسم المستخدم مؤقتًا.",
    GOOGLE_TOKEN_INVALID: "لم يتم التحقق من حساب Google. اختر حساب المطور المعتمد.",
    SESSION_NOT_ESTABLISHED: "تم التحقق من الحساب، لكن جلسة الدخول لم تصل في طلب التأكيد. أعد المحاولة من رابط أدوات المطور المباشر، وإذا استمرت المشكلة أرسل رمز التتبع للدعم.",
  };
  return {
    message: messages[error?.code] || error?.message || "تعذر تسجيل الدخول.",
    requestId: error?.requestId || "",
  };
}

function LoginScreen({ configuration, configurationError, onRetryConfiguration, notice, onPasswordLogin, onGoogleLogin }) {
  const [username, setUsername] = useState(() => {
    try { return localStorage.getItem("n9DeveloperLastUsername") || ""; } catch { return ""; }
  });
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submittingMethod, setSubmittingMethod] = useState("");
  const [error, setError] = useState(null);
  const submissionLockRef = useRef("");
  const submitting = Boolean(submittingMethod);

  const submitPassword = async (event) => {
    event.preventDefault();
    if (submissionLockRef.current) return;
    submissionLockRef.current = "password";
    setError(null);
    setSubmittingMethod("password");
    try {
      await onPasswordLogin({ username: username.trim(), password });
      try { localStorage.setItem("n9DeveloperLastUsername", username.trim()); } catch { /* Optional preference only. */ }
      setPassword("");
    } catch (loginError) {
      setError(describeLoginError(loginError));
    } finally {
      submissionLockRef.current = "";
      setSubmittingMethod("");
    }
  };

  const submitGoogle = async (credential) => {
    if (submissionLockRef.current) return;
    submissionLockRef.current = "google";
    setError(null);
    setSubmittingMethod("google");
    try {
      await onGoogleLogin({ credential });
    } catch (loginError) {
      setError(describeLoginError(loginError));
    } finally {
      submissionLockRef.current = "";
      setSubmittingMethod("");
    }
  };

  return (
    <main className="login-shell">
      <section className="login-brand">
        <div className="brand-mark">N9</div>
        <span>N9 LAW SYSTEM</span>
        <h1>منصة محركات N9</h1>
        <p>القسم التقني · إدارة N9 العامة</p>
        <div className="login-features">
          <span><Database size={22} /><b>قاعدة مستقلة</b><small>لا تعتمد على جداول القضايا والمخالفات</small></span>
          <span><HardDrives size={22} /><b>تشغيل محلي</b><small>المحركات الحكومية تعمل على الجهاز المصرح</small></span>
          <span><ShieldCheck size={22} /><b>دخول مصرح</b><small>صلاحيات المكتب وتراخيص المحركات</small></span>
        </div>
      </section>
      <section className="login-panel">
        <span className="eyebrow"><LockKey size={17} /> منصة شبه مستقلة</span>
        <h2>الدخول إلى منصة المحركات</h2>
        <p>حساب إدارة N9 أو القسم التقني المصرح له.</p>
        <div className="login-primary-methods">
          {notice && <div className="login-error" role="alert"><WarningCircle size={18} /><span>{notice.message}{notice.requestId && <small>رمز التتبع: {notice.requestId}</small>}</span></div>}
          {configurationError && <div className="login-error" role="alert"><span>{configurationError}</span><button type="button" className="button secondary compact" onClick={onRetryConfiguration}><ArrowClockwise size={16} /> إعادة المحاولة</button></div>}
          <span className="login-method-label">الدخول المفضل</span>
          <GoogleSignInButton configuration={configuration?.google} onCredential={(credential) => submitGoogle(credential)} onError={(message) => setError({ message, requestId: "" })} disabled={submitting} />
          {submittingMethod === "google" && <small className="login-progress">جارٍ التحقق من حساب Google وإنشاء الجلسة...</small>}
          <div className="login-divider"><span>أو باسم المستخدم</span></div>
          <form className="login-direct-form" onSubmit={submitPassword} aria-busy={submitting ? "true" : "false"}>
            <label><span>اسم المستخدم</span><div><User size={18} /><input required disabled={submitting} autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} placeholder="اسم المستخدم" /></div></label>
            <label><span>كلمة المرور</span><div><LockKey size={18} /><input required disabled={submitting} autoComplete="current-password" type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="كلمة المرور" /><button type="button" disabled={submitting} title={showPassword ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"} onClick={() => setShowPassword((value) => !value)}>{showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}</button></div></label>
            {error && <div className="login-error"><WarningCircle size={18} /><span>{error.message}{error.requestId && <small>رمز التتبع: {error.requestId}</small>}</span></div>}
            <button className="button primary wide" type="submit" disabled={submitting}>{submittingMethod === "password" ? "جارٍ التحقق وإنشاء الجلسة..." : "تسجيل الدخول"}</button>
          </form>
        </div>
        <div className="login-divider"><span>خيار إضافي</span></div>
        <a className="button secondary wide" href="/auth/start">
          <ArrowSquareOut size={19} /> الدخول الموحد من Workspace
        </a>
        <a className="button secondary wide" href="n9dev://local" title="فتح مركز المحركات المثبت على هذا الجهاز دون إنشاء وظيفة سحابية">
          <DesktopTower size={19} /> تشغيل داخلي
        </a>
        <a className="button text wide" href="/downloads/n9-tools-desktop/N9-Tools-Desktop-Windows-Full.zip" download>
          <DownloadSimple size={18} /> تنزيل أو تحديث التطبيق المحلي
        </a>
        <div className="isolation-note">
          <ShieldCheck size={20} />
          <span><b>N9 LAW SYSTEM</b> منصة المحركات</span>
        </div>
      </section>
    </main>
  );
}

function JobDetails({ job, loading, onClose, onRefresh }) {
  const [packageFile, setPackageFile] = useState(null);
  const [packageProgress, setPackageProgress] = useState('');
  const [packageError, setPackageError] = useState('');
  const [packageBusy, setPackageBusy] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [excelBusy, setExcelBusy] = useState(false);
  const [cloudRetryBusy, setCloudRetryBusy] = useState(false);
  const retryCloud = async () => {
    if (!job?.id || cloudRetryBusy) return;
    setCloudRetryBusy(true);
    setPackageError('');
    try {
      await apiRequest(`/api/v1/jobs/${job.id}/case-packages/cloud-retry`, {method:'POST',body:'{}'});
      setPackageProgress('بدأ استكمال الرفع من الخادم');
      await onRefresh(job.id);
    } catch (error) { setPackageError(error.message || 'تعذر بدء الاستكمال السحابي.'); }
    finally { setCloudRetryBusy(false); }
  };
  const resumeExcel = async () => {
    if (!job?.id || !excelFile || excelBusy) return;
    setExcelBusy(true);
    setPackageError('');
    try {
      await apiRequest(`/api/v1/jobs/${job.id}/result/upload`, {method:'POST',body:excelFile,headers:{'content-type':'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet','x-n9-file-name':excelFile.name},timeoutMs:90_000});
      await onRefresh(job.id);
    } catch (error) { setPackageError(error.message || 'تعذر رفع Excel.'); }
    finally { setExcelBusy(false); }
  };
  const retryCloudExcel = async () => {
    if (!job?.id || cloudRetryBusy) return;
    setCloudRetryBusy(true);
    setPackageError('');
    try {
      await apiRequest(`/api/v1/jobs/${job.id}/result/cloud-retry`, {method:'POST',body:'{}'});
      setPackageProgress('بدأ استكمال رفع Excel من الخادم');
      await onRefresh(job.id);
    } catch (error) { setPackageError(error.message || 'تعذر بدء استكمال Excel.'); }
    finally { setCloudRetryBusy(false); }
  };
  const resumePackages = async () => {
    if (!job?.id || !packageFile || packageBusy) return;
    setPackageBusy(true);
    setPackageError('');
    const base = `/api/v1/jobs/${job.id}/case-packages/uploads`;
    try {
      const session = await apiRequest(base, { method: 'POST', body: JSON.stringify({fileName:packageFile.name,sizeBytes:packageFile.size}) });
      const received = new Set(session.received);
      for (let index = 0; index < session.chunkCount; index += 1) {
        if (received.has(index)) continue;
        const chunk = await packageFile.slice(index * session.chunkSize, Math.min(packageFile.size, (index + 1) * session.chunkSize)).arrayBuffer();
        const hash = [...new Uint8Array(await crypto.subtle.digest('SHA-256', chunk))].map(byte => byte.toString(16).padStart(2, '0')).join('');
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            await apiRequest(`${base}/${session.uploadId}/${index}`, {method:'PUT',body:chunk,headers:{'content-type':'application/octet-stream','x-n9-chunk-sha256':hash},timeoutMs:45_000});
            break;
          } catch (error) { if (attempt === 2) throw error; }
        }
        received.add(index);
        setPackageProgress(`${received.size} / ${session.chunkCount}`);
      }
      await apiRequest(`${base}/${session.uploadId}/finish`, {method:'POST',body:'{}',timeoutMs:45_000});
      setPackageProgress('اكتمل الرفع');
      await onRefresh(job.id);
    } catch (error) { setPackageError(error.message || 'تعذر استكمال الرفع. الأجزاء السابقة محفوظة.'); }
    finally { setPackageBusy(false); }
  };
  if (!job && !loading) return null;
  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="job-dialog" role="dialog" aria-modal="true" aria-label="تفاصيل وظيفة المحرك" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span className="eyebrow">{job?.chapterName || "وظيفة المحرك"}</span>
            <h2>{job?.engineName || "جارٍ التحميل"}</h2>
            <p>{job?.message || "يتم قراءة سجل الوظيفة."}</p>
          </div>
          <div className="dialog-actions">
            {job?.id && <button className="icon-button" type="button" title="تحديث" onClick={() => onRefresh(job.id)}><ArrowClockwise size={20} /></button>}
            <button className="icon-button" type="button" title="إغلاق" onClick={onClose}><X size={20} /></button>
          </div>
        </header>
        {loading && !job ? <div className="loading"><ArrowClockwise className="spin" size={24} /> جارٍ تحميل التفاصيل...</div> : (
          <>
            <div className="job-facts">
              <span>الحالة<b className={`status ${job.status}`}>{jobStatusLabel(job)}</b></span>
              <span>المرحلة<b>{STAGE_LABELS[job.stage] || job.stage}</b></span>
              <span>التقدم<b>{job.progress.current} / {job.progress.total}</b></span>
              <span>المحاولة<b>{job.reliability.attemptCount}</b></span>
            </div>
            <div className="transfer-facts" aria-label="حالة نقل النتائج">
              <span>{job.executionTarget === 'cloud' ? 'السحب على الخادم' : 'السحب المحلي'} <b>{job.transfers?.local ? 'نجح' : 'لم يتأكد'}</b></span>
              <span>رفع Excel <b>{job.transfers?.excel ? 'نجح' : 'لم يكتمل'}</b></span>
              <span>رفع المرفقات <b>{job.transfers?.attachments === 'not_required' ? 'غير مطلوب' : job.transfers?.attachments === 'uploaded' ? 'نجح' : 'بانتظار الاستكمال'}</b></span>
            </div>
            <div className="artifact-actions">
              <a className="button secondary" href={`/api/v1/jobs/${job.id}/input`}><FileXls size={18} /> ملف المدخلات</a>
              <a className={`button secondary ${!job.artifacts.result ? "disabled" : ""}`} href={job.artifacts.result ? `/api/v1/jobs/${job.id}/result` : undefined}><DownloadSimple size={18} /> Excel الناتج</a>
              {job.artifacts.casePackages && <a className="button secondary" href={`/api/v1/jobs/${job.id}/case-packages`}><DownloadSimple size={18} /> ملفات القضايا ZIP</a>}
              <a className={`button secondary ${!job.artifacts.diagnostics ? "disabled" : ""}`} href={job.artifacts.diagnostics ? `/api/v1/jobs/${job.id}/diagnostics` : undefined}><TerminalWindow size={18} /> سجل التشخيص</a>
            </div>
            {job.transfers?.local && !job.transfers?.excel && job.status !== 'running' && <div className="package-recovery">
              {job.executionTarget === 'cloud' && <button className="button secondary" type="button" disabled={cloudRetryBusy} onClick={retryCloudExcel}><CloudArrowUp size={18} /> استكمال Excel من الخادم</button>}
              <label>استكمال رفع Excel الناتج <input type="file" accept=".xlsx" disabled={excelBusy} onChange={event => setExcelFile(event.target.files?.[0] || null)} /></label>
              <button className="button secondary" type="button" disabled={!excelFile || excelBusy} onClick={resumeExcel}><CloudArrowUp size={18} /> رفع Excel</button>
              {packageError && <span role="alert">{packageError}</span>}
            </div>}
            {job.transfers?.attachments === 'pending' && job.transfers?.excel && <div className="package-recovery">
              {job.executionTarget === 'cloud' && <button className="button secondary" type="button" disabled={cloudRetryBusy} onClick={retryCloud}><CloudArrowUp size={18} /> استكمال من الخادم</button>}
              <label>استكمال رفع المرفقات ZIP <input type="file" accept=".zip,application/zip" disabled={packageBusy} onChange={event => setPackageFile(event.target.files?.[0] || null)} /></label>
              <button className="button secondary" type="button" disabled={!packageFile || packageBusy} onClick={resumePackages}><CloudArrowUp size={18} /> استكمال الرفع</button>
              {packageProgress && <span role="status">{packageProgress}</span>}
              {packageError && <span role="alert">{packageError}</span>}
            </div>}
            <div className="timeline">
              <h3>سجل مراحل التشغيل</h3>
              {job.events?.length ? job.events.map((event) => (
                <div className={`timeline-row ${event.level}`} key={event.id}>
                  <span className="timeline-dot" />
                  <div><b>{STAGE_LABELS[event.stage] || event.stage}</b><p>{event.message}</p></div>
                  <time>{formatDate(event.createdAt)}</time>
                </div>
              )) : <div className="empty small">لا توجد أحداث مسجلة بعد.</div>}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (!bytes) return "يُحدد عند التنزيل";
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DesktopCenter({ release, devices, packageUrl }) {
  const downloadUrl = "/downloads/n9-tools-desktop/N9-Tools-Desktop-Windows-Full.zip";
  return (
    <section className="desktop-center">
      <div className="desktop-release-band">
        <div className="desktop-release-copy">
          <span className="eyebrow"><DesktopTower size={18} /> التطبيق المحلي</span>
          <h2>N9 Tools Desktop Console</h2>
          <p>واجهة Windows مستقلة لتشغيل المحركات مباشرة على الجهاز، أو استقبال وظائف منصة المطور عبر البروتوكول الآمن <b dir="ltr">n9dev://</b>.</p>
          <div className="release-facts">
            <span><WindowsLogo size={19} /><b>Windows</b><small>{release?.minimumOs || "Windows 10 أو أحدث"}</small></span>
            <span><Package size={19} /><b>الإصدار {release?.version || "1.24.12"}</b><small>{formatBytes(release?.fullSizeBytes)} · الحزمة الكاملة</small></span>
            <span><ShieldCheck size={19} /><b>تثبيت للمستخدم</b><small>لا يحتاج Administrator</small></span>
          </div>
        </div>
        <div className="desktop-download-actions">
          <a className="button primary wide" href={downloadUrl}><DownloadSimple size={20} /> تنزيل تطبيق Windows</a>
          <a className="button secondary wide" href="/downloads/n9-tools-desktop/README_AR.md" target="_blank" rel="noreferrer"><FolderOpen size={19} /> دليل التثبيت والتشغيل</a>
          {release?.fullSha256 && <code title="بصمة SHA-256">SHA-256: {release.fullSha256.slice(0, 18)}...</code>}
        </div>
      </div>

      <div className="desktop-steps">
        <div><em>1</em><span><b>نزّل وثبّت</b><small>فك الضغط، شغّل Install-N9ToolsDesktop.cmd واختر مجلد التثبيت.</small></span></div>
        <div><em>2</em><span><b>تهيئة تلقائية</b><small>يثبت Python وNode والمكتبات والمتصفح مرة واحدة، ثم ينتظر أوامر التشغيل.</small></span></div>
        <div><em>3</em><span><b>اختر الوحدة والخدمة</b><small>معين، ناجز، التنفيذ أو وحدات الامتثال.</small></span></div>
        <div><em>4</em><span><b>راجع السجل والنتيجة</b><small>كل تشغيل يملك سجلاً وملف نتيجة مستقلين.</small></span></div>
      </div>

      <section className="devices-section">
        <header><div><span className="eyebrow">الأجهزة</span><h2>الأجهزة المحلية المسجلة</h2><p>تظهر بعد استقبال أول وظيفة سحابية من هذا الجهاز.</p></div><span>{devices.length} جهاز</span></header>
        <div className="devices-grid">
          {devices.map((device) => (
            <article key={device.id}>
              <DesktopTower size={24} />
              <div><b>{device.name}</b><small>{device.platform || "Windows"}</small></div>
              <span className={`device-state ${device.status}`}>{device.status === "online" ? "متصل" : "غير متصل"}</span>
              <time>{formatDate(device.lastSeenAt)}</time>
            </article>
          ))}
          {!devices.length && <div className="empty"><DesktopTower size={30} /><b>لم يُسجل جهاز بعد</b><span>ثبّت التطبيق ثم شغّل أول وظيفة من المنصة.</span></div>}
        </div>
      </section>
    </section>
  );
}

function WorkflowCenter({ workflows, engines, onOpenEngine }) {
  const readyCount = workflows.filter((workflow) => workflow.steps.every((step) => {
    const engine = engines.find((item) => item.code === step.engineCode);
    return !step.required || engine?.enabled;
  })).length;
  return (
    <section className="workflow-center">
      <header className="workflow-heading">
        <div><span className="eyebrow"><GitBranch size={18} /> مسارات قانونية محكومة</span><h1>خطة التشغيل قبل فتح المحركات</h1><p>كل خطوة تنتج ملفاً مستقلاً وتنتظر المراجعة قبل الانتقال، حتى يبقى مصدر كل قيمة وقرار واضحاً.</p></div>
        <div className="workflow-summary"><b>{readyCount}</b><span>مسارات قابلة للبدء</span><small>من أصل {workflows.length}</small></div>
      </header>
      <div className="workflow-list">
        {workflows.map((workflow) => {
          const availableSteps = workflow.steps.filter((step) => engines.find((item) => item.code === step.engineCode)?.enabled).length;
          return (
            <article className="workflow-card" key={workflow.code}>
              <header>
                <div><span className={`workflow-state ${workflow.status}`}>{workflow.status === "planned" ? "مخطط" : "تجريبي مضبوط"}</span><h2>{workflow.name}</h2><p>{workflow.description}</p></div>
                <span className="workflow-readiness"><b>{availableSteps}/{workflow.steps.length}</b><small>محركات متاحة</small></span>
              </header>
              <div className="workflow-contract"><span><b>مفتاح المطابقة</b>{workflow.matchKey}</span><span><b>المخرج</b>{workflow.outputDescription}</span></div>
              <ol className="workflow-steps">
                {workflow.steps.map((step) => {
                  const engine = engines.find((item) => item.code === step.engineCode);
                  return <li key={`${workflow.code}-${step.order}`} className={engine?.enabled ? "ready" : "pending"}>
                    <em>{step.order}</em><div><b>{step.name}</b><span>{step.description}</span><small>{step.required ? "إلزامية" : "اختيارية"} · {step.approvalGate ? "تحتاج اعتماداً قبل المتابعة" : "تنتقل تلقائياً"}</small></div>
                    <button className="button secondary compact" type="button" disabled={!engine} onClick={() => onOpenEngine(step.engineCode)}>{engine?.enabled ? "فتح المحرك" : "عرض المخطط"}</button>
                  </li>;
                })}
              </ol>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function GovernanceCenter({ governance, devices, onRevoke, onBackup, onValidateBackup, onRestoreBackup, onTestAlert, onRetryAlerts, busyAction }) {
  const controls = governance?.controls || [];
  const audits = governance?.recentAudit || [];
  const backups = governance?.backups || [];
  const alerting = governance?.alerting || { configured: false, recent: [], pending: 0 };
  const metrics = governance?.metrics || {};
  const backupLabels = { running: "قيد الإنشاء", completed: "مكتملة", failed: "متعذرة" };
  const alertLabels = { pending: "بانتظار الإرسال", delivered: "تم التسليم", failed: "تعذر الإرسال" };
  return (
    <section className="governance-center">
      <header className="governance-heading">
        <div><span className="eyebrow"><ShieldCheck size={18} /> الحوكمة والأمان</span><h1>مركز المراقبة والتحكم</h1><p>ضوابط المنصة، الأجهزة المصرح بها، وأثر كل عملية حساسة في سجل واحد قابل للمراجعة والتصدير.</p></div>
        <a className="button secondary" href="/api/v1/audit/export"><DownloadSimple size={18} /> تصدير سجل التدقيق</a>
      </header>

      <div className="governance-kpis">
        <span><ShieldCheck size={21} /><b>{metrics.enforcedControls || 0}</b><small>ضوابط مطبقة</small></span>
        <span><DesktopTower size={21} /><b>{metrics.activeDevices || 0}</b><small>أجهزة متصلة</small></span>
        <span><LockKey size={21} /><b>{metrics.revokedDevices || 0}</b><small>أجهزة مسحوبة</small></span>
        <span><WarningCircle size={21} /><b>{metrics.failedJobs || 0}</b><small>وظائف متعثرة</small></span>
      </div>

      <div className="continuity-grid">
        <section className="governance-surface backup-surface">
          <header>
            <div><span className="eyebrow">استمرارية الأعمال</span><h2>النسخ الاحتياطي والاستعادة</h2></div>
            <button className="button primary compact" type="button" onClick={onBackup} disabled={Boolean(busyAction)}>
              <Database size={18} /> {busyAction === "backup" ? "جارٍ إنشاء النسخة..." : "إنشاء نسخة الآن"}
            </button>
          </header>
          <div className="continuity-note"><ShieldCheck size={20} /><span><b>قاعدة مستقلة وفحص SHA-256</b><small>الاستعادة مدمجة وتحافظ على السجلات الأحدث غير الموجودة في النسخة، ولا تبدأ أثناء وجود وظائف نشطة.</small></span></div>
          <div className="backup-list">
            {backups.map((backup) => (
              <article className="backup-row" key={backup.id}>
                <span className={`backup-state ${backup.status}`}>{backupLabels[backup.status] || backup.status}</span>
                <div className="backup-main"><b>{backup.trigger === "manual" ? "نسخة يدوية" : "نسخة يومية"}</b><code dir="ltr">{backup.id}</code><small>{formatDate(backup.completedAt || backup.createdAt)}</small></div>
                <div className="backup-facts"><span>{backup.rowCount} سجل</span><span>{backup.artifactCount} مرفق</span><span>{formatBytes(backup.sizeBytes)}</span></div>
                <div className="backup-actions">
                  <a className={`button secondary compact ${backup.status !== "completed" ? "disabled" : ""}`} href={backup.status === "completed" ? `/api/v1/backups/${backup.id}/download` : undefined}><DownloadSimple size={17} /> البيان</a>
                  <button className="button secondary compact" type="button" disabled={backup.status !== "completed" || Boolean(busyAction)} onClick={() => onValidateBackup(backup)}><ShieldCheck size={17} /> فحص</button>
                  <button className="button text compact danger-text" type="button" disabled={backup.status !== "completed" || Boolean(busyAction)} onClick={() => onRestoreBackup(backup)}>استعادة</button>
                </div>
                {backup.error && <p className="backup-error">{backup.error}</p>}
              </article>
            ))}
            {!backups.length && <div className="empty small">لم تُنشأ نسخة احتياطية بعد. سيُنشئ المجدول أول نسخة تلقائياً.</div>}
          </div>
        </section>

        <section className="governance-surface alert-surface">
          <header><div><span className="eyebrow">المراقبة الخارجية</span><h2>التنبيهات الفنية</h2></div><span className={`channel-state ${alerting.configured ? "configured" : "missing"}`}>{alerting.configured ? "القناة متصلة" : "تحتاج إعداداً"}</span></header>
          <div className="alert-channel">
            <div><Pulse size={22} /><span><b>{alerting.channel}</b><small>{alerting.configured ? `${alerting.pending || 0} تنبيه بانتظار التسليم` : "أضف N9_ALERT_WEBHOOK_URL كسر في Cloudflare لتفعيل التسليم الخارجي."}</small></span></div>
            <div className="alert-actions">
              <button className="button secondary compact" type="button" disabled={Boolean(busyAction)} onClick={onTestAlert}>اختبار القناة</button>
              <button className="button text compact" type="button" disabled={Boolean(busyAction) || !alerting.pending} onClick={onRetryAlerts}><ArrowClockwise size={16} /> إعادة المحاولة</button>
            </div>
          </div>
          <div className="alert-list">
            {(alerting.recent || []).slice(0, 8).map((alert) => (
              <article className={`alert-row ${alert.severity}`} key={alert.id}>
                <span className={`alert-status ${alert.status}`}>{alertLabels[alert.status] || alert.status}</span>
                <div><b>{alert.title}</b><small>{alert.code} · {formatDate(alert.createdAt)}</small><p>{alert.message}</p></div>
                <em>{alert.attemptCount} محاولة</em>
              </article>
            ))}
            {!alerting.recent?.length && <div className="empty small">لا توجد تنبيهات فنية مسجلة.</div>}
          </div>
        </section>
      </div>

      <section className="governance-surface">
        <header><div><span className="eyebrow">الضوابط</span><h2>حالة الحماية والجاهزية</h2></div><span>{controls.length} ضابط</span></header>
        <div className="control-list">
          {controls.map((control) => (
            <article key={control.code}>
              <span className={`control-state ${control.status}`}>{CONTROL_LABELS[control.status] || control.status}</span>
              <div><b>{control.name}</b><small>{control.category}</small><p>{control.description}</p></div>
              <code>{control.evidence}</code>
            </article>
          ))}
        </div>
      </section>

      <section className="governance-surface">
        <header><div><span className="eyebrow">الثقة بالأجهزة</span><h2>الأجهزة المحلية</h2></div><span>{devices.length} جهاز</span></header>
        <div className="governance-devices">
          {devices.map((device) => {
            const revoked = Boolean(device.revokedAt) || device.status === "revoked";
            return <article key={device.id}>
              <DesktopTower size={23} />
              <div><b>{device.name}</b><small>{device.platform || "Windows"} · وكيل {device.agentVersion || "غير محدد"}</small></div>
              <span className={`device-state ${revoked ? "revoked" : device.status}`}>{revoked ? "مسحوب" : device.status === "online" ? "متصل" : "غير متصل"}</span>
              <time>{formatDate(device.lastSeenAt)}</time>
              <button className="button text compact danger-text" type="button" disabled={revoked} onClick={() => onRevoke(device)}>{revoked ? "تم السحب" : "سحب الجهاز"}</button>
            </article>;
          })}
          {!devices.length && <div className="empty"><DesktopTower size={30} /><b>لا توجد أجهزة مسجلة</b><span>يظهر الجهاز بعد استقباله أول وظيفة سحابية.</span></div>}
        </div>
      </section>

      <section className="governance-surface audit-surface">
        <header><div><span className="eyebrow">الأثر الرقابي</span><h2>آخر العمليات الحساسة</h2></div><span>{audits.length} حدثاً حديثاً</span></header>
        <div className="audit-table">
          <div className="audit-row audit-head"><span>العملية</span><span>السجل</span><span>الوقت</span><span>معرف الطلب</span></div>
          {audits.map((event) => <div className="audit-row" key={event.id}><span><b>{AUDIT_LABELS[event.action] || event.action}</b><small>{event.action}</small></span><span><b>{event.entityType}</b><small dir="ltr">{event.entityId || "-"}</small></span><span>{formatDate(event.createdAt)}</span><span dir="ltr">{event.requestId || "-"}</span></div>)}
          {!audits.length && <div className="empty small">لا توجد عمليات حساسة مسجلة بعد.</div>}
        </div>
      </section>
    </section>
  );
}

function Dashboard({ identity, overview, onReload, reloading, showToast, onLogout }) {
  const office=useConnectedOfficeBrand(identity.globalAdmin?null:identity.office);
  const globalAdmin = identity.globalAdmin === true;
  const hasPleadings = globalAdmin || overview.engines.some(engine => engine.code === 'pleadings');
  const hasAssistants = globalAdmin || overview.engines.some(engine => engine.code === 'administrative_execution');
  const requestedEngine=overview.engines.find(item=>item.code===new URLSearchParams(window.location.search).get('engine'));
  const [view, setView] = useState(()=>(window.location.hash==='#assistants'||requestedEngine?.code==='administrative_execution')&&hasAssistants?'assistants':window.location.hash==='#pleadings'&&hasPleadings?'pleadings':'engines');
  useEffect(() => {
    if ((view === 'governance' && !globalAdmin) || (view === 'assistants' && !hasAssistants) || (view === 'pleadings' && !hasPleadings)) setView('engines');
  }, [view, globalAdmin, hasAssistants, hasPleadings]);
  useEffect(()=>{window.history.replaceState(null,'',window.location.pathname+window.location.search+(['pleadings','assistants'].includes(view)?'#'+view:''));},[view]);
  const [chapterCode, setChapterCode] = useState(requestedEngine?.unitCode || requestedEngine?.chapterCode || overview.engines[0]?.unitCode || overview.engines[0]?.chapterCode || "");
  const [providerCode, setProviderCode] = useState(requestedEngine?.providerCode || overview.engines[0]?.providerCode || "");
  const [engineCode, setEngineCode] = useState(requestedEngine?.code || overview.engines.find((item) => item.enabled)?.code || "");
  const [runMode, setRunMode] = useState("full");
  const [manualInput, setManualInput] = useState("");
  const [file, setFile] = useState(null);
  const [templateFile, setTemplateFile] = useState(null);
  const [templateSource, setTemplateSource] = useState('library');
  const [templateVersion, setTemplateVersion] = useState('');
  const [templateLibrary, setTemplateLibrary] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState('');
  const [templateReload, setTemplateReload] = useState(0);
  const [pleadingBatchId, setPleadingBatchId] = useState(null);
  const [engineOptions, setEngineOptions] = useState({companyName:'',establishmentNumber:'',partyText:'',headerRow:1,inputColumn:1,columnLayout:'headers',calendar:'gregorian',sheetName:''});
  const [creating, setCreating] = useState(false);
  const [rulingClean, setRulingClean] = useState(null);
  const [launchingId, setLaunchingId] = useState("");
  const launchLinks = useRef(new Map());
  const [selectedJob, setSelectedJob] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [engineQuery, setEngineQuery] = useState("");
  const [engineStatus, setEngineStatus] = useState('all');
  const [inputMode, setInputMode] = useState('excel');
  const [jobEngine, setJobEngine] = useState('all');
  const [jobsPage, setJobsPage] = useState(1);
  const [jobQuery, setJobQuery] = useState("");
  const [jobStatus, setJobStatus] = useState("all");
  const [busyAction, setBusyAction] = useState("");
  const [publishedDesktopRelease, setPublishedDesktopRelease] = useState(null);

  useEffect(() => {
    let active = true;
    fetch("/downloads/n9-tools-desktop/release.json", { cache: "no-store" })
      .then((response) => response.ok ? response.json() : null)
      .then((release) => {
        if (active && /^\d+\.\d+\.\d+$/.test(release?.version || "")) setPublishedDesktopRelease(release);
      }).catch(() => undefined);
    return () => { active = false; };
  }, []);
  const desktopRelease = publishedDesktopRelease ? { ...overview.desktopRelease, ...publishedDesktopRelease } : overview.desktopRelease;

  const chapters = useMemo(() => {
    const map = new Map();
    for (const engine of overview.engines) {
      const code = engine.unitCode || engine.chapterCode;
      const chapter = map.get(code) || { code, name: engine.unitName || engine.chapterName, engines: [] };
      chapter.engines.push(engine);
      map.set(code, chapter);
    }
    return [...map.values()];
  }, [overview.engines]);
  const visibleEngines = overview.engines.filter((engine) => (engine.unitCode || engine.chapterCode) === chapterCode);
  const providers = useMemo(() => {
    const map = new Map();
    for (const item of visibleEngines) {
      const code = item.providerCode || item.chapterCode;
      if (!map.has(code)) map.set(code, { code, name: item.providerName || item.chapterName });
    }
    return [...map.values()];
  }, [visibleEngines]);
  const providerEngines = visibleEngines.filter((item) => (item.providerCode || item.chapterCode) === providerCode);
  const searchedEngines = providerEngines.filter((item) => (engineStatus === 'all' || (engineStatus === 'enabled' ? item.enabled : item.status === engineStatus)) && `${item.name} ${item.description} ${(item.tasks || []).join(" ")}`.toLowerCase().includes(engineQuery.trim().toLowerCase()));
  const engine = providerEngines.find((item) => item.code === engineCode) || providerEngines[0] || visibleEngines[0];
  const runtime = engineRuntime(engine);
  const isDataEngine = ['efaa','pleadings'].includes(engine?.code);
  const isPleading = engine?.code === 'pleadings';
  const isJeddah = engine?.code === 'jeddah_municipality';
  const isJeddahInquiry = engine?.code === 'jeddah_inquiry';
  const usesSavedTemplate = isPleading && templateSource === 'library';
  const inputLimitMB = usesSavedTemplate ? 15 : 30;
  useEffect(() => {
    if (!isPleading || view !== 'engines') return;
    let active = true;
    setTemplateLoading(true);
    setTemplateError('');
    apiRequest('/api/v1/pleadings').then(data => {
      if (!active) return;
      const versions = data.versions || [];
      setTemplateLibrary(versions);
      setTemplateVersion(id => versions.some(v => v.id === id && v.status === 'approved') ? id : '');
    }).catch(error => {
      if (active) { setTemplateLibrary([]); setTemplateError(error.message); }
    }).finally(() => { if (active) setTemplateLoading(false); });
    return () => { active = false; };
  }, [isPleading, view, templateReload]);
  const updateEngineOption = (key,value) => setEngineOptions(previous=>({...previous,[key]:value}));
  const downloadUrl = '/downloads/n9-tools-desktop/N9-Tools-Desktop-Windows-Full.zip';
  const engineRelations = (overview.engineRelations || []).filter((relation) => relation.sourceEngineCode === engine?.code || relation.targetEngineCode === engine?.code);
  const activeJobs = overview.jobs.filter((job) => ["queued", "running"].includes(job.status));
  const filteredJobs = overview.jobs.filter((job) => {
    const matchesStatus = jobStatus === "all" || job.status === jobStatus;
    const haystack = `${job.engineName} ${job.chapterName} ${job.message} ${job.id}`.toLowerCase();
    return matchesStatus && (jobEngine === 'all' || job.engineCode === jobEngine) && haystack.includes(jobQuery.trim().toLowerCase());
  });
  const pageCount = Math.max(1, Math.ceil(filteredJobs.length / 10));
  const currentPage = Math.min(jobsPage, pageCount);
  const pageJobs = filteredJobs.slice((currentPage-1)*10, currentPage*10);
  useEffect(()=>setJobsPage(1),[jobQuery,jobStatus,jobEngine]);
  useEffect(()=>{ setFile(null); setTemplateFile(null); setManualInput(''); setRunMode('full'); setInputMode('excel'); },[engine?.code]);
  useEffect(()=>{if (isJeddah) setEngineOptions({jeddahMode:'audit',headerRow:0,sheetName:'',attachmentTitle:'مرفقات الاعتراض',attachmentSource:'',confirmSubmission:false});},[isJeddah]);
  useEffect(()=>{if (isJeddah) setEngineOptions(previous=>({...previous,confirmSubmission:false}));},[file,isJeddah]);
  useEffect(()=>{if (isJeddahInquiry) setEngineOptions({inquiryMode:'inquiry',sheetName:'',browserCount:1,pagesPerBrowser:1,testLimit:3});},[isJeddahInquiry]);

  const openEngine = (code) => {
    const target = overview.engines.find((item) => item.code === code);
    if (!target) return;
    setChapterCode(target.unitCode || target.chapterCode);
    setProviderCode(target.providerCode || target.chapterCode);
    setEngineCode(target.code);
    setEngineQuery("");
    setView("engines");
  };

  useEffect(() => {
    if (!providers.some((item) => item.code === providerCode)) {
      setProviderCode(providers[0]?.code || "");
    }
  }, [providerCode, providers]);

  useEffect(() => {
    if (!providerEngines.some((item) => item.code === engineCode)) {
      setEngineCode(providerEngines.find((item) => item.enabled)?.code || providerEngines[0]?.code || "");
    }
  }, [engineCode, providerEngines]);

  const createJob = async () => {
    if (!engine?.enabled) return showToast("هذا المحرك مخطط ولم يُفتح للتشغيل بعد.");
    if (engine.code === 'moeen_rulings' && !rulingClean?.accepted) return showToast('راجع منظف الأرقام وحدد عمود الدعوى قبل إنشاء الوظيفة.');
    const inputFile = inputMode === 'excel' ? file : null;
    let manual;
    try { manual = referenceInput(engine,inputMode === 'manual' ? manualInput : ''); } catch(error) { return showToast(error.message); }
    if (!inputFile && !manual.inputRowCount && !["najiz_agencies_agent", "najiz_agencies_principal", "najiz_cases"].includes(engine.code)) {
      return showToast(isJeddah ? 'ارفع Excel الذي يحتوي أرقام السداد ونصوص الاعتراض.' : "ارفع ملف Excel أو أدخل الأرقام المرجعية، كل رقم في سطر.");
    }
    setCreating(true);
    try {
      if (usesSavedTemplate) {
        if (!inputFile) throw new Error('ارفع ملف Excel لبيانات اللائحة.');
        const payload = savedPleadingPayload(templateVersion, templateLibrary, engineOptions);
        const form = new FormData();
        form.set('file', inputFile);
        form.set('payload', JSON.stringify(payload));
        const result = await apiRequest('/api/v1/pleadings/batches', {method: 'POST', body: form});
        setPleadingBatchId(result.id);
        setFile(null);
        setView('pleadings');
        showToast('تم تجهيز الدفعة بالقالب المحفوظ؛ راجع البيانات ثم ولّد المسودات.');
        return;
      }
      if (isJeddah && !inputFile) throw new Error('ارفع Excel الذي يحتوي أرقام السداد ونصوص الاعتراض.');
      const configuredOptions = dataEngineOptions(engine.code, isDataEngine || isJeddah || isJeddahInquiry || ['moeen_rulings','moeen_screenshots'].includes(engine.code) ? {...engineOptions,...(engine.code==='moeen_screenshots'?{executionTarget:'local'}:{}),...(inputMode==='manual'?{headerRow:1,inputColumn:1}:{})} : {});
      if(isPleading && !templateFile) throw new Error('اختر قالب DOCX للائحة المنظورة.');
      const form = new FormData();
      form.set("payload", JSON.stringify({
        engineCode: engine.code,
        inputSheetName: manual.inputRowCount ? "مدخلات يدوية" : inputFile ? "ملف Excel" : "تشغيل كامل",
        inputRowCount: manual.inputRowCount,
        inputColumns: manual.inputRowCount ? manual.inputColumns : engine.inputColumns,
        previewRows: manual.previewRows,
        options: { ...configuredOptions, runMode, workflowRequestId: new URLSearchParams(window.location.search).get('workflow') || undefined },
      }));
      if (inputFile) form.set("file", inputFile);
      if (isPleading && templateFile) form.set('template',templateFile);
      const result = await apiRequest("/api/v1/jobs", { method: "POST", body: form });
      setFile(null);
      setManualInput("");
      showToast("أُنشئت الوظيفة في المنصة المستقلة وأصبحت جاهزة للتشغيل.");
      await onReload(true);
      await showJob(result.jobId);
    } catch (error) {
      showToast(error.message);
    } finally {
      setCreating(false);
    }
  };

  const launchJob = async (jobId, download = false, target = 'local') => {
    setLaunchingId(jobId);
    const popup = target === 'cloud' ? window.open('about:blank','_blank') : null;
    if (popup) popup.opener = null;
    try {
      if (target === 'cloud') {
        const result = await apiRequest(`/api/v1/jobs/${jobId}/launch`, {method:'POST',body:JSON.stringify({executionTarget:'cloud'})});
        if(popup) popup.location.replace(result.viewUrl);
        else showToast('تم طلب التشغيل. اسمح بالنوافذ المنبثقة ثم اضغط «نافذة السحابة».');
        await onReload(true);
        return;
      }
      let result = launchLinks.current.get(jobId);
      const job = overview.jobs.find(item=>item.id === jobId);
      if (job?.status !== 'queued' || !result || Date.parse(result.expiresAt) <= Date.now()) {
        result = await apiRequest(`/api/v1/jobs/${jobId}/launch`, { method: "POST", body: JSON.stringify({executionTarget:'local'}) });
        launchLinks.current.set(jobId, result);
      }
      if (download) {
        const url = URL.createObjectURL(new Blob([buildLaunchCommand(result.launcherUrl)],{type:'application/octet-stream'}));
        const link = document.createElement('a');
        link.href = url; link.download = `N9-Run-${jobId}.cmd`; link.click();
        window.setTimeout(()=>URL.revokeObjectURL(url),10000);
        showToast('افتح ملف التشغيل: يتحقق من أحدث نسخة ويثبتها قبل بدء المهمة. يحتوي تصريحاً مؤقتاً؛ لا تشاركه.');
      } else {
        triggerProtocol(result.launcherUrl);
        showToast('طُلب فتح الطرفية. لن تبدأ الوظيفة حتى يتصل التطبيق المحلي؛ إذا لم تفتح، استخدم تنزيل ملف التشغيل.');
      }
      window.setTimeout(() => onReload(true), 1600);
    } catch (error) {
      popup?.close();
      showToast(error.message);
    } finally {
      setLaunchingId("");
    }
  };

  const openCloudView = async jobId => {
    const popup=window.open('about:blank','_blank'); if(popup) popup.opener=null;
    try {
      const result=await apiRequest(`/api/v1/jobs/${jobId}/cloud-view`,{method:'POST',body:'{}'});
      if(popup) popup.location.replace(result.viewUrl);
      else showToast('اسمح بالنوافذ المنبثقة لفتح نافذة نفاذ.');
    } catch(error){popup?.close();showToast(error.message);}
  };

  const showJob = async (jobId, silent = false) => {
    if (!silent) setDetailLoading(true);
    try {
      const result = await apiRequest(`/api/v1/jobs/${jobId}`);
      setSelectedJob(result.job);
    } catch (error) {
      if (!silent) showToast(error.message);
    } finally {
      if (!silent) setDetailLoading(false);
    }
  };
  useEffect(()=>{
    const jobId=new URLSearchParams(window.location.search).get('job');
    if(!jobId) return;
    showJob(jobId);
    const params=new URLSearchParams(window.location.search);
    params.delete('job');
    window.history.replaceState(null,'',window.location.pathname+(params.size?`?${params}`:'')+window.location.hash);
  },[]);

  const cancelJob = async (jobId) => {
    if (!window.confirm("إلغاء هذه الوظيفة ومنع الوكيل من استكمالها؟")) return;
    try {
      await apiRequest(`/api/v1/jobs/${jobId}/cancel`, { method: "POST", body: "{}" });
      showToast("أُلغيت الوظيفة.");
      await onReload(true);
    } catch (error) {
      showToast(error.message);
    }
  };

  const revokeDevice = async (device) => {
    if (!window.confirm(`سحب الجهاز ${device.name}؟ ستتوقف وظائفه النشطة ولن يستطيع الاتصال مجدداً بهذا المعرف.`)) return;
    try {
      await apiRequest(`/api/v1/devices/${encodeURIComponent(device.id)}/revoke`, { method: "POST", body: "{}" });
      showToast(`تم سحب الجهاز ${device.name} وإيقاف تصاريحه النشطة.`);
      await onReload(true);
    } catch (error) {
      showToast(error.message);
    }
  };

  const createBackup = async () => {
    setBusyAction("backup");
    try {
      const result = await apiRequest("/api/v1/backups", { method: "POST", body: "{}" });
      showToast(`اكتملت النسخة الاحتياطية: ${result.backup.rowCount} سجل و${result.backup.artifactCount} مرفق.`);
      await onReload(true);
    } catch (error) {
      showToast(error.message);
    } finally {
      setBusyAction("");
    }
  };

  const validateBackup = async (backup) => {
    setBusyAction(`validate:${backup.id}`);
    try {
      const result = await apiRequest(`/api/v1/backups/${backup.id}/restore`, { method: "POST", body: JSON.stringify({ mode: "dry_run" }) });
      const summary = result.summary;
      showToast(`النسخة سليمة: ${summary.rowCount} سجل، ${summary.artifactCount} مرفق، والبصمة متطابقة.`);
    } catch (error) {
      showToast(error.message);
    } finally {
      setBusyAction("");
    }
  };

  const restoreBackup = async (backup) => {
    const confirmation = window.prompt("عملية حساسة: اكتب معرف النسخة كاملاً لتنفيذ استعادة مدمجة.", "");
    if (confirmation == null) return;
    if (confirmation.trim() !== backup.id) return showToast("لم يطابق التأكيد معرف النسخة؛ لم تُنفذ الاستعادة.");
    setBusyAction(`restore:${backup.id}`);
    try {
      const result = await apiRequest(`/api/v1/backups/${backup.id}/restore`, { method: "POST", body: JSON.stringify({ mode: "apply", confirmation }) });
      showToast(`اكتملت الاستعادة: ${result.summary.restoredRows} سجل و${result.summary.restoredArtifacts} مرفق.`);
      await onReload(true);
    } catch (error) {
      showToast(error.message);
    } finally {
      setBusyAction("");
    }
  };

  const testAlert = async () => {
    setBusyAction("alert-test");
    try {
      const result = await apiRequest("/api/v1/alerts/test", { method: "POST", body: "{}" });
      showToast(result.configured ? "أُرسل تنبيه الاختبار إلى القناة الخارجية." : "سُجل التنبيه، لكن وجهة Webhook الخارجية لم تُهيأ بعد.");
      await onReload(true);
    } catch (error) {
      showToast(error.message);
    } finally {
      setBusyAction("");
    }
  };

  const retryAlerts = async () => {
    setBusyAction("alert-retry");
    try {
      const result = await apiRequest("/api/v1/alerts/retry", { method: "POST", body: "{}" });
      showToast(result.delivery.configured ? `تم تسليم ${result.delivery.delivered} وتعذر ${result.delivery.failed}.` : "وجهة Webhook الخارجية لم تُهيأ بعد.");
      await onReload(true);
    } catch (error) {
      showToast(error.message);
    } finally {
      setBusyAction("");
    }
  };

  useEffect(() => {
    if (!activeJobs.length) return undefined;
    const refreshVisible = () => {
      if (document.visibilityState === "visible") onReload(true);
    };
    const interval = window.setInterval(refreshVisible, 15000);
    document.addEventListener("visibilitychange", refreshVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshVisible);
    };
  }, [activeJobs.length, onReload]);

  useEffect(() => {
    if (!selectedJob?.id || !["queued", "running"].includes(selectedJob.status)) return undefined;
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") showJob(selectedJob.id, true);
    }, 5000);
    return () => window.clearInterval(interval);
  }, [selectedJob?.id, selectedJob?.status]);

  return (
    <div className="app-shell engine-console" style={!globalAdmin?connectedOfficeTheme(office):undefined}>
      <header className="topbar">
        {!globalAdmin&&office?<ConnectedOfficeBrand office={office} product="القسم التقني · محركات المكتب"/>:<div className="brand"><span>N9</span><div><b>{globalAdmin ? 'إدارة محركات N9' : 'محركات المكتب'}</b><small>{globalAdmin ? 'إدارة N9 العامة' : identity.context?.legalDepartment?.name}</small></div></div>}
        <nav className="portal-nav" aria-label="أقسام منصة المحركات">
          <button type="button" className={view === "engines" ? "active" : ""} onClick={() => setView("engines")}><TerminalWindow size={18} /> المحركات والوظائف</button>
          {hasPleadings && <button type="button" className={view === 'pleadings' ? 'active' : ''} onClick={()=>setView('pleadings')}><FileDoc size={18}/> نظام اللوائح</button>}
          {hasAssistants && <button type="button" className={view === 'assistants' ? 'active' : ''} onClick={()=>setView('assistants')}><GitBranch size={18}/> الأنظمة المساعدة</button>}
          <button type="button" className={view === "workflows" ? "active" : ""} onClick={() => setView("workflows")}><GitBranch size={18} /> المسارات القانونية</button>
          <button type="button" className={view === "desktop" ? "active" : ""} onClick={() => setView("desktop")}><DesktopTower size={18} /> التطبيق المحلي</button>
          {globalAdmin && <button type="button" className={view === "governance" ? "active" : ""} onClick={() => setView("governance")}><ShieldCheck size={18} /> الحوكمة والمراقبة</button>}
        </nav>
        <div className="topbar-actions">
          <span className="connection"><ShieldCheck size={17} /> معزولة تشغيليًا</span>
          <a className="button secondary compact" href="n9dev://local" title="تشغيل المحركات مباشرة على هذا الجهاز دون إنشاء وظيفة في السحابة"><DesktopTower size={18} /> تشغيل داخلي</a>
          <button className="button secondary" type="button" onClick={() => onReload()} disabled={reloading}><ArrowClockwise className={reloading ? "spin" : ""} size={18} /> تحديث</button>
          <button className="icon-button" type="button" title="تسجيل الخروج" onClick={onLogout}><SignOut size={20} /></button>
        </div>
      </header>

      <main className="workspace">
        <div className="topbar-actions" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
          <a className="button secondary" href="https://n9-cases.n9-law-system.workers.dev/" target="_blank" rel="noreferrer"><ArrowSquareOut size={18} /> تحديث ناجز داخل القضايا</a>
          <a className="button secondary" href="https://n9-law-workspace.n9-law-system.workers.dev/workspace/violations" target="_blank" rel="noreferrer"><ArrowSquareOut size={18} /> تحديث معين داخل Workspace</a>
        </div>
        {view === 'assistants' && hasAssistants ? <AdministrativeExecution cloud={overview.cloud} onPleadings={()=>{setPleadingBatchId(null);setView('pleadings');}} onJob={async id=>{setView('engines');await onReload();await showJob(id);}}/> : view === 'pleadings' && hasPleadings ? <Suspense fallback={<p role="status">تحميل نظام اللوائح…</p>}><PleadingStudio initialBatchId={pleadingBatchId}/></Suspense> : view === "desktop" ? (
          <DesktopCenter release={desktopRelease} devices={overview.devices} packageUrl={overview.agent?.windowsPackageUrl} />
        ) : view === "governance" && globalAdmin ? (
          <GovernanceCenter
            governance={overview.governance}
            devices={overview.devices}
            onRevoke={revokeDevice}
            onBackup={createBackup}
            onValidateBackup={validateBackup}
            onRestoreBackup={restoreBackup}
            onTestAlert={testAlert}
            onRetryAlerts={retryAlerts}
            busyAction={busyAction}
          />
        ) : view === "workflows" ? (
          <WorkflowCenter workflows={overview.workflows || []} engines={overview.engines} onOpenEngine={openEngine} />
        ) : <>
        <section className="page-heading">
          <div><span className="eyebrow">{globalAdmin ? 'إدارة N9 العامة' : identity.context?.legalDepartment?.name}</span><h1>{globalAdmin ? 'مركز المحركات' : 'محركات المكتب'}</h1></div>
          <div className="identity"><span>{identity.user.displayName}</span><small>{identity.user.username}</small></div>
        </section>

        <section className="engine-release-strip" aria-label="تنزيل المحرك">
          <Package size={26} /><div><b>التطبيق المحلي <span dir="ltr">{desktopRelease?.version || '1.24.12'}</span></b><small>Windows 10/11 x64 · حزمة كاملة تشمل Python وNode والمكتبات والمتصفح · تختار مجلد التثبيت ويُحفظ للتشغيل والتحديث</small></div>
          <a className="button primary" href={downloadUrl}><DownloadSimple size={18} /> تنزيل وتثبيت</a>
          <button className="button secondary" onClick={()=>setView('desktop')}><DesktopTower size={18} /> تفاصيل الإصدار والأجهزة</button>
        </section>

        <section className="kpis" aria-label="حالة المحركات">
          <div><HardDrives size={22} /><span>الأجهزة المسجلة<b>{overview.devices.length}</b></span></div>
          <div><Pulse size={22} /><span>تشغيل حي<b>{activeJobs.length}</b></span></div>
          <div><CheckCircle size={22} /><span>نتائج محفوظة<b>{overview.jobs.filter((job) => job.status === "completed").length}</b></span></div>
          <div><WarningCircle size={22} /><span>تحتاج تدخل<b>{overview.jobs.filter((job) => job.status === "failed" || job.reliability.attentionRequired).length}</b></span></div>
        </section>

        <section className="control-layout">
          <nav className="chapters" aria-label="فصول المحركات">
            <header><SquaresFour size={18} /> وحدات الخدمات</header>
            {chapters.map((chapter) => (
              <button type="button" key={chapter.code} className={chapter.code === chapterCode ? "active" : ""} onClick={() => setChapterCode(chapter.code)}>
                <span>{chapter.name}</span><em>{chapter.engines.length}</em>
              </button>
            ))}
          </nav>

          <div className="engine-workspace">
            <div className="provider-tabs" role="tablist" aria-label="مزودو الخدمة">
              {providers.map((provider) => (
                <button type="button" role="tab" aria-selected={provider.code === providerCode} key={provider.code} className={provider.code === providerCode ? "active" : ""} onClick={() => setProviderCode(provider.code)}>
                  {provider.name}
                </button>
              ))}
            </div>
            <div className="engine-catalog-tools">
              <label className="engine-search"><MagnifyingGlass size={18} /><input aria-label="البحث عن محرك" value={engineQuery} onChange={(event) => setEngineQuery(event.target.value)} placeholder="اسم المحرك أو المهمة" /></label>
              <select aria-label="تصفية حالة المحرك" value={engineStatus} onChange={event=>setEngineStatus(event.target.value)}><option value="all">كل المحركات</option><option value="enabled">متاحة للتشغيل</option><option value="experimental">تجريبية</option><option value="planned">مخططة</option></select>
            </div>
            <div className="engine-console-grid">
            <div className="engine-list">
              {searchedEngines.map((item) => (
                <button type="button" aria-pressed={item.code === engine?.code} key={item.code} className={`${item.code === engine?.code ? "active" : ""} ${!item.enabled ? "disabled" : ""}`} onClick={() => item.code==='administrative_execution'?setView('assistants'):setEngineCode(item.code)}>
                  <span><TerminalWindow size={18} /><b>{item.name}</b></span>
                  <small>{item.description}</small>
                  <span className="engine-row-meta"><em className={item.status}>{item.enabled ? (item.status === "ready" ? "متاح" : "تجريبي") : "مخطط"}</em><small>{engineRuntime(item).label}</small></span>
                </button>
              ))}
              {!searchedEngines.length && <div className="empty small"><MagnifyingGlass size={24} /><b>لا توجد نتيجة مطابقة</b><span>جرّب اسم منصة أو مهمة مختلفة.</span></div>}
            </div>

            {engine?.code==='moeen_memoranda' && <section className="composer">
              <header><div><span className="eyebrow">معين · تبادل المذكرات</span><h2>تجهيز حزمة المذكرة</h2><p>من ملف الدعوى في Workspace</p></div></header>
              <div className="engine-contract"><span><small>المخرجات</small><b>ZIP · نص المذكرة وبياناتها ومرفقات PDF</b></span><span><small>حالة الربط بمعين</small><b>التعبئة والتقديم غير مفعّلين</b></span></div>
              <a className="button secondary" href="https://n9-law-workspace.n9-law-system.workers.dev/workspace/judicial-followup" target="_blank" rel="noreferrer"><ArrowSquareOut size={18}/> ملفات الدعاوى في Workspace</a>
            </section>}
            {engine?.code === 'violation_prepare' && engine.enabled && <Suspense fallback={<p role="status">تحميل محرك تجهيز المخالفات...</p>}><ViolationPreparation/></Suspense>}
            {engine && !['moeen_memoranda', 'violation_prepare'].includes(engine.code) && <section className="composer">
              <header>
                <div><span className="eyebrow">{engine.unitName || engine.chapterName} · {engine.providerName || engine.chapterName}</span><h2>{engine.name}</h2><p>{engine.description}</p></div>
                <a className="button secondary" href={usesSavedTemplate ? '/api/v1/pleadings/input-template' : `/api/v1/engines/${encodeURIComponent(engine.code)}/template`}><DownloadSimple size={18} /> قالب Excel</a>
              </header>
              <div className="engine-contract"><span><small>بيئة التشغيل</small><b>{usesSavedTemplate ? 'داخل المنصة · قالب محفوظ معتمد' : runtime.requirement}</b></span><span><small>المخرجات</small><b>{runtime.output}</b></span></div>
              {engine.status === 'experimental' && <div className="engine-caution"><WarningCircle size={18} /> محرك تجريبي؛ راجع النتائج قبل استخدامها.</div>}
              {engine.tasks?.length ? <details className="engine-tasks">
                <summary>مهام المحرك ({engine.tasks.length})</summary>
                <h3><ListChecks size={18} /> مهام المحرك</h3>
                <div>{engine.tasks.map((task, index) => <span key={`${engine.code}-${task}`}><em>{index + 1}</em>{task}</span>)}</div>
              </details> : null}
              {engineRelations.length ? <details className="engine-relations">
                <summary>خدمات مرتبطة ({engineRelations.length})</summary>
                <h3><LinkSimple size={18} /> خدمات مرتبطة</h3>
                {engineRelations.map((relation) => {
                  const otherCode = relation.sourceEngineCode === engine.code ? relation.targetEngineCode : relation.sourceEngineCode;
                  const other = overview.engines.find((item) => item.code === otherCode);
                  return <span key={`${relation.sourceEngineCode}-${relation.targetEngineCode}-${relation.code}`}><b>{other?.name || otherCode} · {relation.name}</b><small>{relation.description}</small></span>;
                })}
              </details> : null}
              {isDataEngine && <fieldset className="data-engine-options"><legend>إعدادات {isPleading ? 'اللائحة' : 'استعلام إيفاء'}</legend>
                {isPleading && <>
                  <div className="engine-input-tabs full-field" role="group" aria-label="مصدر قالب اللائحة">
                    <button type="button" aria-pressed={templateSource==='library'} onClick={()=>setTemplateSource('library')}><GitBranch size={18}/> القوالب المحفوظة</button>
                    <button type="button" aria-pressed={templateSource==='upload'} onClick={()=>setTemplateSource('upload')}><FileDoc size={18}/> قالب خارجي</button>
                  </div>
                  {usesSavedTemplate && <label className="full-field"><span>القالب المعتمد</span><select aria-label="القالب المعتمد" value={templateVersion} disabled={templateLoading || !!templateError} onChange={e=>setTemplateVersion(e.target.value)}><option value="">{templateLoading?'تحميل القوالب...':'اختر القالب'}</option>{templateLibrary.filter(v=>v.status==='approved').map(v=><option key={v.id} value={v.id}>{v.name} · إصدار {v.version}</option>)}</select></label>}
                  {usesSavedTemplate && templateError && <p className="full-field" role="alert">{templateError}<button type="button" className="button secondary" onClick={()=>setTemplateReload(n=>n+1)}><ArrowClockwise size={18}/> إعادة المحاولة</button></p>}
                  {usesSavedTemplate && !templateLoading && !templateError && !templateLibrary.some(v=>v.status==='approved') && <p className="full-field">لا توجد قوالب معتمدة في حسابك. أضف قالبًا أو اعتمد إصدارًا من المكتبة.</p>}
                  <button type="button" className="button secondary full-field" onClick={()=>{setPleadingBatchId(null);setView('pleadings');}}><GitBranch size={18}/> إدارة القوالب والدفعات</button>
                </>}
                <label><span>اسم الشركة</span><input value={engineOptions.companyName} maxLength={200} onChange={e=>updateEngineOption('companyName',e.target.value)} /></label>
                {!isPleading && <label><span>رقم المنشأة</span><input inputMode="numeric" dir="ltr" value={engineOptions.establishmentNumber} onChange={e=>updateEngineOption('establishmentNumber',e.target.value)} placeholder="7xxxxxxxxx" /></label>}
                <label><span>اسم ورقة Excel (اختياري)</span><input value={engineOptions.sheetName} maxLength={31} onChange={e=>updateEngineOption('sheetName',e.target.value)} placeholder="أول ورقة افتراضيًا" /></label>
                {inputMode==='excel' && <label><span>{usesSavedTemplate?'صف عناوين Excel':'صف العنوان (0 دون عناوين)'}</span><input type="number" min={usesSavedTemplate?1:0} max="100" value={engineOptions.headerRow} onChange={e=>updateEngineOption('headerRow',e.target.value)} /></label>}
                {!isPleading && inputMode==='excel' && <label><span>رقم عمود السند (A = 1)</span><input type="number" min="1" max="16384" value={engineOptions.inputColumn} onChange={e=>updateEngineOption('inputColumn',e.target.value)} /></label>}
                {isPleading && <>
                  {!usesSavedTemplate && <><label><span>ربط أعمدة المصدر</span><select value={engineOptions.columnLayout} onChange={e=>updateEngineOption('columnLayout',e.target.value)}><option value="headers">حسب عناوين النموذج</option><option value="10">نموذج 10 أعمدة</option><option value="13">نموذج 13 عمودًا</option></select></label>
                  <label><span>تقويم التاريخ غير المسمّى</span><select value={engineOptions.calendar} onChange={e=>updateEngineOption('calendar',e.target.value)}><option value="gregorian">ميلادي</option><option value="hijri">هجري</option></select></label></>}
                  <label className="full-field"><span>نص المعترض كاملًا</span><textarea rows="3" value={engineOptions.partyText} maxLength={5000} onChange={e=>updateEngineOption('partyText',e.target.value)} /></label>
                  {!usesSavedTemplate && <><label className="full-field"><span>قالب لائحة المنظورة DOCX</span><input aria-label="قالب اللائحة" type="file" accept=".docx" onChange={e=>{const selected=e.target.files?.[0]; if(selected && (!/\.docx$/i.test(selected.name)||selected.size>10*1024*1024)){e.target.value='';return showToast('اختر قالب DOCX لا يتجاوز 10MB.');} setTemplateFile(selected || null);}} /></label>
                  <p className="full-field">مسودات للمراجعة فقط. يلزم القالب المتوافق مع محرك اللوائح؛ قالب الحكم النهائي غير متاح في مسار القالب الخارجي.</p></>}
                </>}
              </fieldset>}
              {isJeddah && <JeddahControls options={engineOptions} setOptions={setEngineOptions} globalAdmin={globalAdmin}/>}
              {isJeddahInquiry && <JeddahInquiryControls options={engineOptions} setOptions={setEngineOptions}/>}
              <div className="engine-input-tabs" role="group" aria-label="مصدر المدخلات">
                <button type="button" aria-pressed={inputMode === 'excel'} onClick={()=>setInputMode('excel')}><FileXls size={18}/> ملف Excel</button>
                {!isPleading && !isJeddah && <button type="button" aria-pressed={inputMode === 'manual'} onClick={()=>setInputMode('manual')}><ListChecks size={18}/> أرقام مباشرة</button>}
              </div>
              <div className="form-grid">
                {!usesSavedTemplate && !isJeddah && <label><span>نمط التشغيل</span><select value={runMode} onChange={(event) => setRunMode(event.target.value)} disabled={!engine.enabled}><option value="full">تشغيل كامل</option><option value="refresh">تحديث وإعادة فحص</option><option value="test">تشغيل تجريبي محدود</option></select></label>}
                {inputMode === 'manual' && <label><span>{engine.inputColumns?.[0] || 'أرقام أو مراجع مباشرة'} · {manualInput.split(/\r?\n/).filter(value=>value.trim()).length} / 200</span><textarea rows="5" value={manualInput} onChange={(event) => setManualInput(event.target.value)} placeholder="كل رقم في سطر مستقل" disabled={!engine.enabled} /></label>}
              </div>
              {inputMode === 'excel' && <div className={`upload ${file ? "selected" : ""}`}>
                <FileXls size={30} />
                <div><b>{file?.name || "استيراد مدخلات Excel"}</b><small>{file ? `${Math.ceil(file.size / 1024)} KB` : `XLSX حتى ${inputLimitMB}MB في التخزين المستقل`}</small></div>
                <label className="button secondary"><CloudArrowUp size={18} /> {file ? "استبدال الملف" : "اختيار ملف"}<input key={engine.code} aria-label="ملف مدخلات المحرك" type="file" accept=".xlsx" onChange={(event) => { const next=event.target.files?.[0]; event.target.value=''; if(!next) return; if(!/\.xlsx$/i.test(next.name) || next.size>inputLimitMB*1024*1024) { showToast(`اختر ملف XLSX لا يتجاوز ${inputLimitMB}MB.`); return; } setFile(next); }} disabled={!engine.enabled} /></label>
                {file && <button className="icon-button" aria-label="إزالة ملف المدخلات" title="إزالة ملف المدخلات" onClick={()=>setFile(null)}><X size={16}/></button>}
              </div>}
              {engine.code === 'moeen_rulings' && <Suspense fallback={<p>جارٍ تحميل منظف الأرقام...</p>}><RulingControls file={file} inputMode={inputMode} manualInput={manualInput} options={engineOptions} setOptions={setEngineOptions} cloud={overview.cloud} onReport={setRulingClean}/></Suspense>}
              {engine.code === 'moeen_screenshots' && <section className="capture-options" aria-label="خيارات صور معين">
                <fieldset><legend>وضع التصوير</legend><div className="view-switch" role="group" aria-label="وضع التصوير">
                  <button type="button" className={(engineOptions.captureMode || 'documentation')==='documentation'?'active':''} aria-pressed={(engineOptions.captureMode || 'documentation')==='documentation'} onClick={()=>updateEngineOption('captureMode','documentation')}>التوثيق</button>
                  <button type="button" className={engineOptions.captureMode==='execution'?'active':''} aria-pressed={engineOptions.captureMode==='execution'} onClick={()=>updateEngineOption('captureMode','execution')}>التنفيذ</button>
                </div></fieldset>
                <dl><div><dt>النطاق</dt><dd>{engineOptions.captureMode==='execution'?'البيانات الأساسية والموضوع، الجلسات والأحكام، الطلبات والاستئناف':'جميع أقسام الاستعلام التفصيلي وصفحات جداولها'}</dd></div><div><dt>درجات التقاضي</dt><dd>الابتدائي وجميع الاستئنافات والعليا المتاحة</dd></div><div><dt>المخرجات</dt><dd>صور أصلية وفهرس Excel وحزمة ZIP لكل رقم موحد</dd></div></dl>
                <p className="muted">الصور للتوثيق، ولا تعني اعتماد الحكم النهائي المختوم. تسجيل نفاذ يدوي. التشغيل المحلي يتطلب الإصدار 1.10.0 أو أحدث.</p>
              </section>}
              <footer><span><ShieldCheck size={18} /><b>نطاق مستقل:</b> ملفات Excel وحزم القضايا ZIP، بلا كتابة تلقائية في Workspace.</span><button className="button primary" type="button" onClick={createJob} disabled={!engine.enabled || creating || (usesSavedTemplate && (templateLoading || !!templateError || !templateVersion)) || (engine.code==='moeen_rulings' && !rulingClean?.accepted)}><Play size={18} /> {creating ? "جارٍ التجهيز..." : usesSavedTemplate ? 'تجهيز اللوائح بالقالب المختار' : engine.code==='moeen_rulings' ? `إنشاء وظيفة للأرقام الصحيحة (${rulingClean?.accepted || 0})` : "إنشاء وظيفة"}</button></footer>
            </section>}
            </div>
          </div>
        </section>

        <section className="jobs-section" id="engine-jobs">
          <header><div><span className="eyebrow">السجل التشغيلي</span><h2>وظائف المحركات</h2><p>كل وظيفة مستقلة بمدخلاتها ونتائجها وسجل مراحلها.</p></div><span>{filteredJobs.length} من {overview.jobs.length}</span></header>
          <div className="jobs-filters">
            <label><MagnifyingGlass size={17} /><input value={jobQuery} onChange={(event) => setJobQuery(event.target.value)} placeholder="ابحث بالمحرك أو الرسالة أو رقم الوظيفة" /></label>
            <label><FunnelSimple size={17} /><select value={jobStatus} onChange={(event) => setJobStatus(event.target.value)}><option value="all">كل الحالات</option><option value="ready">جاهزة</option><option value="queued">بانتظار الجهاز</option><option value="running">قيد التشغيل</option><option value="completed">مكتملة</option><option value="failed">متعذرة</option></select></label>
            <select aria-label="محرك الوظائف" value={jobEngine} onChange={event=>setJobEngine(event.target.value)}><option value="all">كل المحركات</option>{overview.engines.map(item=><option value={item.code} key={item.code}>{item.name}</option>)}</select>
          </div>
          <div className="jobs-table">
            <div className="jobs-row jobs-head"><span>المحرك</span><span>الحالة والمرحلة</span><span>التقدم</span><span>الجهاز</span><span>آخر تحديث</span><span>الإجراءات</span></div>
            {pageJobs.map((job) => (
              <div className="jobs-row" key={job.id}>
                <span><b>{job.engineName}</b><small>{job.chapterName} · {RUN_MODE_LABELS[job.runMode] || job.runMode} · {job.executionTarget==='cloud'?'سحابي':'محلي'}</small></span>
                  <span><b className={`status ${job.status}`}>{jobStatusLabel(job)}</b><small>{STAGE_LABELS[job.stage] || job.stage}</small></span>
                <span><b>{job.progress.current} / {job.progress.total}</b><small>{job.reliability.heartbeatAgeSeconds == null ? "دون نبض" : `نبض قبل ${job.reliability.heartbeatAgeSeconds} ث`}</small></span>
                <span><b>{job.device?.name || "لم يتصل"}</b><small>{job.device?.agentVersion || ""}</small></span>
                <span><b>{formatDate(job.updatedAt)}</b><small>{job.message}</small></span>
                <span className="row-actions">
                  {job.executionTarget==='cloud' && ['queued','running'].includes(job.status) && <button type="button" className="button secondary compact" onClick={()=>openCloudView(job.id)}><ArrowSquareOut size={16}/> نافذة السحابة</button>}
                  {['ready','failed','queued'].includes(job.status) && !(job.executionTarget==='cloud' && job.status==='queued') ? <>
                    {!job.options?.executionRecordSource && ['moeen_rulings','administrative_execution'].includes(job.engineCode) && <button className="button secondary compact" type="button" disabled={!overview.cloud?.configured || launchingId===job.id} title={overview.cloud?.configured?'تشغيل في الخادم دون تنزيل':'يلزم ربط خادم المحركات'} onClick={()=>launchJob(job.id,false,'cloud')}><CloudArrowUp size={16}/> تشغيل سحابي</button>}
                    <button className="button primary compact" type="button" onClick={() => launchJob(job.id,false,job.executionTarget||'local')} disabled={launchingId === job.id || (job.executionTarget==='cloud'&&!overview.cloud?.configured)}><Play size={16} /> {job.executionTarget==='cloud'?'بدء المهمة السحابية':job.status === 'queued' ? 'فتح الطرفية مجدداً' : 'تشغيل محلي'}</button>
                    <button className="icon-button" type="button" title="تنزيل وتشغيل بأحدث نسخة" aria-label="تنزيل وتشغيل بأحدث نسخة" disabled={launchingId === job.id} onClick={()=>launchJob(job.id,true)}><DownloadSimple size={18} /></button>
                  </> : null}
                  <button className="button text compact" type="button" onClick={() => showJob(job.id)}>التفاصيل</button>
                  {job.status !== "completed" && job.status !== "cancelled" ? <button className="icon-button danger" type="button" title="إلغاء" onClick={() => cancelJob(job.id)}><XCircle size={18} /></button> : null}
                </span>
              </div>
            ))}
            {!filteredJobs.length && <div className="empty"><Clock size={30} /><b>لا توجد وظائف مطابقة</b><span>غيّر البحث أو الحالة، أو أنشئ وظيفة جديدة.</span></div>}
          </div>
          <footer className="jobs-pagination"><span>{filteredJobs.length} وظيفة · 10 صفوف في الصفحة</span><button className="icon-button" disabled={currentPage === 1} onClick={()=>setJobsPage(currentPage-1)} aria-label="الصفحة السابقة" title="الصفحة السابقة"><ArrowRight size={18}/></button><b>{currentPage} / {pageCount}</b><button className="icon-button" disabled={currentPage === pageCount} onClick={()=>setJobsPage(currentPage+1)} aria-label="الصفحة التالية" title="الصفحة التالية"><ArrowLeft size={18}/></button></footer>
        </section>
        </>}
      </main>

      <JobDetails job={selectedJob} loading={detailLoading} onClose={() => setSelectedJob(null)} onRefresh={showJob} />
    </div>
  );
}

export function App() {
  const [authLoading, setAuthLoading] = useState(true);
  const [identity, setIdentity] = useState(null);
  const [overview, setOverview] = useState(null);
  const [overviewError, setOverviewError] = useState("");
  const [reloading, setReloading] = useState(false);
  const [toast, setToast] = useState("");
  const [authConfiguration, setAuthConfiguration] = useState({ google: { enabled: false, clientId: null } });
  const [configurationError, setConfigurationError] = useState("");
  const [notice, setNotice] = useState(() => loginNotice(window.location.search));
  const authConfigRequestRef = useRef(0);
  const loadAuthConfiguration = useCallback(async () => {
    const id = ++authConfigRequestRef.current;
    setConfigurationError("");
    try {
      const configuration = await apiRequest("/api/auth/config", { retry: 1, timeoutMs: 20_000 });
      if (id === authConfigRequestRef.current) setAuthConfiguration(configuration);
    } catch (error) {
      if (id === authConfigRequestRef.current) setConfigurationError("تعذر تحميل خيارات Google. يمكنك إعادة المحاولة أو الدخول باسم المستخدم.");
    }
  }, []);

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 3500);
  }, []);

  const loadOverview = useCallback(async (silent = false) => {
    setReloading(true);
    setOverviewError("");
    try {
      const result = await apiRequest("/api/v1/overview");
      setOverview(result);
    } catch (error) {
      if ([401, 403].includes(error.status) || error.code === "PASSWORD_CHANGE_REQUIRED") {
        setIdentity(null);
        setOverview(null);
        setNotice(describeLoginError(error));
      }
      else { setOverviewError(error.message); showToast(error.message); }
    } finally {
      setReloading(false);
    }
  }, [showToast]);

  useEffect(() => {
    let active = true;
    loadAuthConfiguration();
    const params = new URLSearchParams(window.location.search);
    if (params.has("authError")) {
      params.delete("authError"); params.delete("ref");
      window.history.replaceState({}, "", `${window.location.pathname}${params.size ? `?${params}` : ""}${window.location.hash}`);
    }
    (async () => {
      try {
        const me = await apiRequest("/api/auth/me", { retry: 0, timeoutMs: 20_000 });
        if (!active) return;
        setIdentity(me);
        const result = await apiRequest("/api/v1/overview", { retry: 1, timeoutMs: 12_000 });
        if (active) setOverview(result);
      } catch (error) {
        if (active && error.status !== 401) { setOverviewError(error.message); setNotice(describeLoginError(error)); showToast(error.message); }
        if (active && [401, 403].includes(error.status)) { setIdentity(null); setOverview(null); }
      } finally {
        if (active) setAuthLoading(false);
      }
    })();
    return () => { active = false; authConfigRequestRef.current += 1; };
  }, [showToast, loadAuthConfiguration]);

  const logout = async () => {
    try { await confirmedLogout(() => apiRequest("/api/auth/logout", { method: "POST", body: "{}" })); }
    catch (error) { showToast(`تعذر تأكيد تسجيل الخروج. أعد المحاولة. ${error.message}`); }
  };

  const loginDirectly = async (path, body) => {
    await apiRequest(path, { method: "POST", body: JSON.stringify(body), timeoutMs: 45_000 });
    let verified;
    try {
      verified = await apiRequest("/api/auth/me", { retry: 0, timeoutMs: 20_000 });
    } catch (error) {
      if (error.code === "UNAUTHENTICATED") error.code = "SESSION_NOT_ESTABLISHED";
      throw error;
    }
    setNotice(null);
    setIdentity(verified);
    await loadOverview();
  };

  if (authLoading) return <div className="splash"><div className="brand-mark">N9</div><ArrowClockwise className="spin" size={24} /><span>جارٍ التحقق من جلسة المطور...</span></div>;
  if (!identity) return <><LoginScreen configuration={authConfiguration} configurationError={configurationError} onRetryConfiguration={loadAuthConfiguration} notice={notice} onPasswordLogin={(body) => loginDirectly("/api/auth/login", body)} onGoogleLogin={(body) => loginDirectly("/api/auth/google", body)} /><Toast message={toast} /></>;
  if (!overview) return <div className="splash">
    {overviewError ? <WarningCircle size={28} /> : <ArrowClockwise className="spin" size={24} />}
    <strong>{overviewError ? "تم تسجيل الدخول، وتعذر تحميل لوحة المحركات" : "جارٍ تحميل منصة المحركات..."}</strong>
    {overviewError && <><p role="alert">{overviewError}</p><button className="button" type="button" disabled={reloading} onClick={() => loadOverview()}><ArrowClockwise /> إعادة المحاولة</button><button className="button secondary" type="button" onClick={logout}>تغيير الحساب</button></>}
  </div>;
  return <><Dashboard identity={identity} overview={overview} onReload={loadOverview} reloading={reloading} showToast={showToast} onLogout={logout} /><Toast message={toast} /></>;
}
